import { useMemo, useState, Fragment } from "react";
import { Wrench, Plus, Trash2, Save, Pencil, X, ChevronRight, ChevronDown } from "lucide-react";
import PartTypesEditor from "./PartTypesEditor";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { HarvestComponentRow } from "@/hooks/useHarvestComponents";

interface ApplianceCategory {
  id: string;
  name: string;
}

interface Props {
  categories: ApplianceCategory[];
}

interface FormState {
  name: string;
  description: string;
  default_weight_kg: string;
  default_value: string;
  default_recovery_pct: string;
  is_hazardous: boolean;
}

const emptyForm: FormState = {
  name: "",
  description: "",
  default_weight_kg: "",
  default_value: "",
  default_recovery_pct: "50",
  is_hazardous: false,
};

function rowToForm(r: HarvestComponentRow): FormState {
  return {
    name: r.name || "",
    description: r.description || "",
    default_weight_kg: String(r.default_weight_kg ?? ""),
    default_value: String(r.default_value ?? ""),
    default_recovery_pct: String(r.default_recovery_pct ?? "50"),
    is_hazardous: !!r.is_hazardous,
  };
}

/**
 * Super Admin base-logic management: define the standard list of spare-part
 * categories per appliance type with full metadata. Rows are inserted with
 * is_system_default=true and company_id=null; downstream company admins can
 * see them but cannot edit/delete them.
 */
export default function SpartPartBaseLogicSection({ categories }: Props) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string>(categories[0]?.name || "");
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);
  const [expandedId, setExpandedId] = useState<string | null>(null);


  const effectiveSelected = useMemo(() => {
    if (selected) return selected;
    return categories[0]?.name || "";
  }, [selected, categories]);

  const { data: defaults = [], isLoading } = useQuery({
    queryKey: ["harvest_components_defaults", effectiveSelected],
    enabled: !!effectiveSelected,
    queryFn: async (): Promise<HarvestComponentRow[]> => {
      const { data, error } = await supabase
        .from("harvest_components")
        .select("*")
        .eq("appliance_category", effectiveSelected)
        .eq("is_system_default", true)
        .order("name");
      if (error) throw error;
      return (data || []) as HarvestComponentRow[];
    },
  });

  const buildPayload = (f: FormState) => ({
    name: f.name.trim(),
    description: f.description.trim() || null,
    category: null,
    material: null,
    default_weight_kg: parseFloat(f.default_weight_kg) || 0,
    default_value: parseFloat(f.default_value) || 0,
    default_recovery_pct: parseFloat(f.default_recovery_pct) || 50,
    is_hazardous: f.is_hazardous,
  });

  const saveDefault = useMutation({
    mutationFn: async ({ form, id }: { form: FormState; id?: string }) => {
      const payload = buildPayload(form);
      if (id) {
        const { error } = await supabase
          .from("harvest_components")
          .update(payload)
          .eq("id", id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("harvest_components").insert({
          ...payload,
          appliance_category: effectiveSelected,
          is_system_default: true,
          company_id: null,
        } as any);
        if (error) throw error;
      }
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["harvest_components_defaults"] });
      queryClient.invalidateQueries({ queryKey: ["harvest_components"] });
      if (vars.id) {
        setEditingId(null);
        toast.success("Default part updated");
      } else {
        setShowNew(false);
        setNewForm(emptyForm);
        toast.success("Default part added");
      }
    },
    onError: (e: any) => toast.error(e.message || "Save failed"),
  });

  const deleteDefault = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("harvest_components").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["harvest_components_defaults"] });
      queryClient.invalidateQueries({ queryKey: ["harvest_components"] });
      toast.success("Default part removed");
    },
    onError: (e: any) => toast.error(e.message || "Delete failed"),
  });

  const renderForm = (
    form: FormState,
    setForm: (f: FormState) => void,
    onSave: () => void,
    onCancel: () => void,
  ) => (
    <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input
          placeholder="Name *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <div className="flex items-center gap-2">
          <Checkbox
            checked={form.is_hazardous}
            onCheckedChange={(c) => setForm({ ...form, is_hazardous: !!c })}
          />
          <span className="text-sm">Hazardous</span>
        </div>
        <Input
          placeholder="Weight (kg)"
          type="number"
          step="0.1"
          value={form.default_weight_kg}
          onChange={(e) => setForm({ ...form, default_weight_kg: e.target.value })}
        />
        <Input
          placeholder="Value (€)"
          type="number"
          step="0.01"
          value={form.default_value}
          onChange={(e) => setForm({ ...form, default_value: e.target.value })}
        />
        <Input
          placeholder="Recovery %"
          type="number"
          min="0"
          max="100"
          value={form.default_recovery_pct}
          onChange={(e) => setForm({ ...form, default_recovery_pct: e.target.value })}
        />
      </div>
      <Textarea
        placeholder="Description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        rows={2}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={onSave}
          disabled={!form.name.trim() || saveDefault.isPending}
          className="gap-1"
        >
          <Save className="w-3.5 h-3.5" /> Save
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="gap-1">
          <X className="w-3.5 h-3.5" /> Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <section className="px-6 mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Wrench className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">Spare-Part Base Logic per Appliance Type</h2>
        <Badge variant="secondary">{defaults.length}</Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Standard list of part categories shipped with each appliance type. These flow
        downstream into Harvesting Management and the Inventory filter and cannot be
        edited or deleted by company admins.
      </p>

      <div className="bg-card rounded-xl border border-border p-4 mb-4 space-y-3">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Appliance Type</label>
          <SearchableSelect
            className="max-w-sm"
            value={effectiveSelected || null}
            onChange={(v) => { setSelected(v ?? ""); setEditingId(null); setShowNew(false); }}
            placeholder="Choose an appliance type…"
            searchPlaceholder="Search appliance types…"
            emptyMessage="No appliance types found"
            items={categories.map((c) => ({ value: c.name, label: c.name }))}
          />
        </div>
      </div>

      {effectiveSelected && !showNew && (
        <Button
          variant="outline"
          className="gap-2 border-dashed w-full mb-3"
          onClick={() => { setShowNew(true); setNewForm(emptyForm); setEditingId(null); }}
        >
          <Plus className="w-4 h-4" /> Add Default Part
        </Button>
      )}
      {showNew && (
        <div className="mb-3">
          {renderForm(
            newForm,
            setNewForm,
            () => saveDefault.mutate({ form: newForm }),
            () => { setShowNew(false); setNewForm(emptyForm); },
          )}
        </div>
      )}

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Weight (kg)</TableHead>
              <TableHead className="text-right">Value (€)</TableHead>
              <TableHead className="text-right">Recovery %</TableHead>
              <TableHead>Hazardous</TableHead>
              <TableHead className="w-[110px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!effectiveSelected ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                  Pick an appliance type to view its base parts.
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                  Loading…
                </TableCell>
              </TableRow>
            ) : defaults.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                  No default parts defined for {effectiveSelected}.
                </TableCell>
              </TableRow>
            ) : (
              defaults.map((d) => {
                if (editingId === d.id) {
                  return (
                    <TableRow key={d.id}>
                      <TableCell colSpan={7} className="p-3">
                        {renderForm(
                          editForm,
                          setEditForm,
                          () => saveDefault.mutate({ form: editForm, id: d.id }),
                          () => setEditingId(null),
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }
                const isOpen = expandedId === d.id;
                return (
                  <Fragment key={d.id}>
                    <TableRow>
                      <TableCell className="pr-0">
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={() => setExpandedId(isOpen ? null : d.id)}
                          aria-label={isOpen ? "Collapse" : "Expand"}
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell className="text-right text-xs">{d.default_weight_kg ?? 0}</TableCell>
                      <TableCell className="text-right text-xs">{d.default_value ?? 0}</TableCell>
                      <TableCell className="text-right text-xs">{d.default_recovery_pct ?? 50}</TableCell>
                      <TableCell>
                        {d.is_hazardous ? (
                          <Badge variant="destructive" className="text-[10px]">Hazardous</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => { setEditingId(d.id); setEditForm(rowToForm(d)); setShowNew(false); }}
                            aria-label={`Edit ${d.name}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              if (confirm(`Remove "${d.name}" from ${effectiveSelected} defaults?`)) {
                                deleteDefault.mutate(d.id);
                              }
                            }}
                            aria-label={`Delete ${d.name}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow key={`${d.id}-exp`}>
                        <TableCell colSpan={7} className="p-0">
                          <PartTypesEditor partsCategoryId={d.id} partsCategoryName={d.name} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </section>

  );
}
